// @flow
import { useEffect, useReducer, useState } from 'react';
import { useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useValidation, useSubmitError, hasUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

type ShouldResetCaptchaState = {|
  shouldReset: ?boolean,
  submitted: ?boolean,
  hasErrors: ?boolean,
|};

type ShouldResetCaptchaAction = {|
  type: string,
  payload?: any,
|};

function reduceShouldResetCaptcha(state: ShouldResetCaptchaState, action: ShouldResetCaptchaAction): ShouldResetCaptchaState {
  switch (action.type) {
    case 'setSubmitted':
      return { shouldReset: Boolean(state.hasErrors && action.payload) || null, submitted: action.payload, hasErrors: null };

    case 'setHasErrors':
      return { shouldReset: Boolean(state.submitted && action.payload) || null, hasErrors: action.payload, submitted: null };

    case 'clear':
      return { shouldReset: null, submitted: null, hasErrors: null };

    default: return state;
  }
}

export function useAutoResetCaptcha(): boolean {
  const incomingPayment = useIncomingPayment();
  const submitting = useSubmitting();
  const validation = useValidation();
  const submitError = useSubmitError();
  const hasUserInputError = hasUserInputValidationError(validation);

  const [state, dispatch] = useReducer(reduceShouldResetCaptcha, {
    shouldReset: null,
    submitted: null,
    hasErrors: null,
  });

  const [shouldReset, setShouldReset] = useState(state.shouldReset);

  useEffect(() => {
    dispatch({ type: 'setSubmitted', payload: true });
    dispatch({ type: 'setHasErrors', payload: true });
  }, [incomingPayment]);

  useEffect(() => {
    dispatch({ type: 'setSubmitted', payload: !submitting });
  }, [submitting]);

  useEffect(() => {
    dispatch({ type: 'setHasErrors', payload: !!submitError || hasUserInputError });
  }, [submitError, hasUserInputError]);

  useEffect(() => {
    if (state.shouldReset) {
      setShouldReset(true);
      dispatch({ type: 'clear' });
    } else {
      setShouldReset(false);
    }
  }, [state]);

  return !!shouldReset;
}
